import { useState } from 'react';
import {
  Link as RouterLink,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { format, formatDistanceToNow } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { useRequest, useRequestMutations } from './useRequests';

/** Within how many days "expires soon" turns red. */
const EXPIRES_SOON_DAYS = 3;
const NEW_BADGE_WINDOW_MS = 24 * 60 * 60 * 1000;

export function RequestDetailPage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;
  const { id = '' } = useParams<{ id: string }>();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useNotification();
  const { translate } = useApiError();

  const { request, isLoading } = useRequest(id);
  const { resolve, remove, respond } = useRequestMutations();

  const [responseText, setResponseText] = useState('');
  const [showResponseInput, setShowResponseInput] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!request) return <Navigate to="/requests" replace />;

  const createdAtMs = new Date(request.createdAt).getTime();
  const expiresAtMs = new Date(request.expiresAt).getTime();
  const postedAgo = formatDistanceToNow(createdAtMs, { addSuffix: true, locale: dateLocale });
  const expiresOn = format(new Date(request.expiresAt), 'd MMMM yyyy', { locale: dateLocale });
  const daysToExpiry = (expiresAtMs - Date.now()) / (24 * 60 * 60 * 1000);
  const expiresSoon =
    request.status === 'ACTIVE' && daysToExpiry > 0 && daysToExpiry < EXPIRES_SOON_DAYS;
  const isNew = Date.now() - createdAtMs < NEW_BADGE_WINDOW_MS;
  const isAnonymousOwner = request.owner.userId === null;
  const ownerInitial = (request.owner.displayName || '?').trim().charAt(0).toUpperCase();
  const locationLabel = [request.cityName, request.countryCode].filter(Boolean).join(' • ');

  const handleRespond = async () => {
    if (!isAuthenticated) {
      navigate(
        `/register?next=${encodeURIComponent(`/requests/${request.id}`)}`,
        { state: { from: location } },
      );
      return;
    }
    if (isAnonymousOwner) return;
    try {
      const conv = await respond.mutateAsync({
        id: request.id,
        body: responseText.trim() || undefined,
      });
      navigate(`/messages/${conv.id}`);
    } catch (err) {
      notify.error(translate(err));
    }
  };

  const handleResolve = async () => {
    try {
      await resolve.mutateAsync(request.id);
      notify.success(t('requests.resolvedNotice'));
    } catch (err) {
      notify.error(translate(err));
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('requests.deleteConfirm'))) return;
    try {
      await remove.mutateAsync(request.id);
      notify.success(t('requests.deletedNotice'));
      navigate('/requests', { replace: true });
    } catch (err) {
      notify.error(translate(err));
    }
  };

  const canRespond = request.status === 'ACTIVE' && !request.isOwner;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
        <Button
          component={RouterLink}
          to="/requests"
          startIcon={<ArrowBackRoundedIcon />}
          sx={{ mb: 2, fontWeight: 600 }}
        >
          {t('requests.backToList')}
        </Button>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.06)',
          }}
        >
          {/* HERO HEADER — image when set, themed gradient otherwise. Floating
              badges show recency, status and response count without crowding
              the body of the page. */}
          <Box
            sx={{
              position: 'relative',
              height: { xs: 200, md: 280 },
              background: (theme) =>
                request.imageUrl
                  ? undefined
                  : `linear-gradient(135deg, ${theme.palette.primary.main}28 0%, ${theme.palette.secondary.main}28 50%, ${theme.palette.primary.main}3a 100%)`,
              backgroundImage: request.imageUrl ? `url(${request.imageUrl})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            {!request.imageUrl && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                  opacity: 0.5,
                }}
              >
                <HelpOutlineRoundedIcon sx={{ fontSize: 120 }} />
              </Box>
            )}
            {request.imageUrl && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  background:
                    'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.5) 100%)',
                }}
              />
            )}

            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
              sx={{ position: 'absolute', top: 16, left: 16 }}
            >
              {isNew && request.status === 'ACTIVE' && (
                <Chip
                  icon={<AutoAwesomeRoundedIcon sx={{ fontSize: '0.95rem !important' }} />}
                  label={t('requests.newBadge')}
                  size="small"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'common.white',
                    fontWeight: 700,
                    '& .MuiChip-icon': { color: 'common.white' },
                  }}
                />
              )}
              {request.status !== 'ACTIVE' && (
                <Chip
                  size="small"
                  label={t(`requests.status.${request.status.toLowerCase()}`)}
                  sx={{
                    bgcolor: request.status === 'RESOLVED' ? 'success.main' : 'grey.700',
                    color: 'common.white',
                    fontWeight: 700,
                  }}
                />
              )}
            </Stack>

            {request.responseCount > 0 && (
              <Chip
                icon={<GroupRoundedIcon sx={{ fontSize: '0.95rem !important' }} />}
                label={t('requests.responsesCount', { count: request.responseCount })}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  bgcolor: 'rgba(255,255,255,0.95)',
                  color: 'text.primary',
                  fontWeight: 700,
                  backdropFilter: 'blur(4px)',
                }}
              />
            )}
          </Box>

          {/* CONTENT */}
          <Box sx={{ p: { xs: 2.5, md: 4 } }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                lineHeight: 1.15,
                mb: 2,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
              }}
            >
              {request.title}
            </Typography>

            {/* META row — author, location, posted ago. */}
            <Stack
              direction="row"
              spacing={2.5}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              sx={{ mb: 3 }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'primary.main',
                    fontWeight: 700,
                    fontSize: '1rem',
                  }}
                >
                  {ownerInitial}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {request.owner.displayName || t('requests.anonymousOwnerLabel')}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                    <AccessTimeRoundedIcon sx={{ fontSize: '0.85rem' }} />
                    <Typography variant="caption">{postedAgo}</Typography>
                  </Stack>
                </Box>
              </Stack>
              {locationLabel && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }}>
                  <LocationOnRoundedIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                    {locationLabel}
                  </Typography>
                </Stack>
              )}
            </Stack>

            {request.description && (
              <Box
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                  px: { xs: 2, md: 2.5 },
                  py: { xs: 2, md: 2.5 },
                  mb: 3,
                }}
              >
                <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                  {request.description}
                </Typography>
              </Box>
            )}

            {/* Expiration chip — turns red when within EXPIRES_SOON_DAYS so
                the urgency is obvious without reading the date. */}
            <Chip
              icon={<EventBusyRoundedIcon />}
              label={t('requests.expiresOn', { date: expiresOn })}
              sx={{
                mb: 3,
                bgcolor: expiresSoon ? 'error.main' : 'action.hover',
                color: expiresSoon ? 'common.white' : 'text.secondary',
                fontWeight: expiresSoon ? 700 : 500,
                '& .MuiChip-icon': { color: expiresSoon ? 'common.white' : 'text.secondary' },
              }}
            />

            {/* RESPONSE area — divider above to set it apart from the body. */}
            {canRespond && (
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3 }}>
                {isAnonymousOwner ? (
                  isAuthenticated && request.ownerContact ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: { xs: 2, md: 2.5 },
                        borderRadius: 3,
                        bgcolor: (theme) => `${theme.palette.primary.main}0d`,
                        borderColor: 'primary.main',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                        {t('requests.anonViewerContactTitle', {
                          name: request.owner.displayName ?? '',
                        })}
                      </Typography>
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            <EmailRoundedIcon sx={{ fontSize: '1.1rem' }} />
                          </Avatar>
                          <Link
                            href={`mailto:${request.ownerContact.email}`}
                            sx={{ fontWeight: 600 }}
                          >
                            {request.ownerContact.email}
                          </Link>
                        </Stack>
                        {request.ownerContact.phone && (
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'success.main' }}>
                              <PhoneRoundedIcon sx={{ fontSize: '1.1rem' }} />
                            </Avatar>
                            <Link
                              href={`tel:${request.ownerContact.phone}`}
                              sx={{ fontWeight: 600 }}
                            >
                              {request.ownerContact.phone}
                            </Link>
                          </Stack>
                        )}
                      </Stack>
                    </Paper>
                  ) : (
                    <RespondCTA
                      onClick={handleRespond}
                      loading={false}
                      label={t('requests.respondAction')}
                      hint={t('requests.respondHint')}
                    />
                  )
                ) : showResponseInput ? (
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {t('requests.respondAction')}
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      placeholder={t('requests.responsePlaceholder')}
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      inputProps={{ maxLength: 4000 }}
                      autoFocus
                    />
                    <Stack direction="row" spacing={1}>
                      <Button onClick={() => setShowResponseInput(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Box sx={{ flex: 1 }} />
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<HandshakeRoundedIcon />}
                        onClick={handleRespond}
                        disabled={respond.isPending}
                      >
                        {respond.isPending ? t('common.loading') : t('requests.sendResponse')}
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <RespondCTA
                    onClick={() => {
                      if (!isAuthenticated) handleRespond();
                      else setShowResponseInput(true);
                    }}
                    loading={false}
                    label={t('requests.respondAction')}
                    hint={t('requests.respondHint')}
                  />
                )}
              </Box>
            )}

            {/* OWNER actions — resolve / delete. */}
            {request.isOwner && request.status === 'ACTIVE' && (
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3, mt: 3 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  {t('requests.ownerActionsLabel')}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    variant="outlined"
                    startIcon={<CheckCircleRoundedIcon />}
                    onClick={handleResolve}
                    disabled={resolve.isPending}
                  >
                    {t('requests.resolveAction')}
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    startIcon={<DeleteOutlineRoundedIcon />}
                    onClick={handleDelete}
                    disabled={remove.isPending}
                  >
                    {t('requests.deleteAction')}
                  </Button>
                </Stack>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

/** Big primary CTA + helper hint underneath. Used in both the registered-
 *  owner and anonymous-owner paths since the visual is identical. */
function RespondCTA({
  onClick,
  loading,
  label,
  hint,
}: {
  onClick: () => void;
  loading: boolean;
  label: string;
  hint: string;
}) {
  return (
    <Stack spacing={1}>
      <Button
        variant="contained"
        size="large"
        startIcon={<HandshakeRoundedIcon />}
        onClick={onClick}
        disabled={loading}
        fullWidth
        sx={{
          py: 1.75,
          fontWeight: 700,
          fontSize: '1rem',
          boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
        }}
      >
        {label}
      </Button>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: 'center', display: 'block' }}
      >
        {hint}
      </Typography>
    </Stack>
  );
}
